import { useLingui } from '@lingui/react/macro'
import { cva, type VariantProps } from 'class-variance-authority'
import { Command as CommandPrimitive } from 'cmdk'
import { cn } from 'designSystem/lib/utils'
import { SearchIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot='command'
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className
      )}
      {...props}
    />
  )
}

const commandDialogContentVariants = cva(
  'fixed top-[50%] left-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 overflow-hidden rounded-lg border bg-popover p-0 text-popover-foreground shadow-lg duration-150 ease-out data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-98 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-98',
  {
    variants: {
      size: {
        default: 'max-w-lg',
        lg: 'max-w-2xl',
      },
      instantClose: {
        true: 'data-[state=closed]:animate-none',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      instantClose: false,
    },
  }
)

function CommandDialog({
  title,
  description,
  children,
  className,
  commandProps,
  size,
  instantClose,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root> &
  VariantProps<typeof commandDialogContentVariants> & {
    title?: string
    description?: string
    className?: string
    /** Forwarded to the inner cmdk root, e.g. `shouldFilter`, `value`, `onKeyDown`. */
    commandProps?: Omit<
      React.ComponentProps<typeof CommandPrimitive>,
      'children'
    >
  }) {
  const { t } = useLingui()
  return (
    <DialogPrimitive.Root data-slot='command-dialog' {...props}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          data-size={size ?? 'default'}
          className={cn(
            commandDialogContentVariants({ size, instantClose }),
            className
          )}>
          <DialogPrimitive.Title className='sr-only'>
            {title ?? t`Command Palette`}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className='sr-only'>
            {description ?? t`Search for a command to run...`}
          </DialogPrimitive.Description>
          <Command
            {...commandProps}
            className={cn(
              '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5',
              commandProps?.className
            )}>
            {children}
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

const commandInputWrapperVariants = cva('flex items-center border-b', {
  variants: {
    inputSize: {
      default: 'h-9 gap-2 px-3 [&_svg]:size-4',
      lg: 'h-14 gap-3 px-5 [&_svg]:size-5',
    },
  },
  defaultVariants: { inputSize: 'default' },
})

const commandInputVariants = cva(
  'flex w-full rounded-md bg-transparent py-3 outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      inputSize: {
        default: 'mobile-input-text h-10 text-base md:text-sm',
        lg: 'h-14 text-lg',
      },
    },
    defaultVariants: { inputSize: 'default' },
  }
)

function CommandInput({
  className,
  endAdornment,
  inputSize,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> &
  VariantProps<typeof commandInputVariants> & {
    /**
     * Trailing slot inside the bordered search row (after the input), e.g. an
     * info glyph -- it participates in the wrapper's flex row.
     */
    endAdornment?: React.ReactNode
  }) {
  return (
    <div
      data-slot='command-input-wrapper'
      data-size={inputSize ?? 'default'}
      className={commandInputWrapperVariants({ inputSize })}
      cmdk-input-wrapper=''>
      <SearchIcon className='shrink-0 opacity-50' />
      <CommandPrimitive.Input
        data-size={inputSize ?? 'default'}
        data-slot='command-input'
        className={cn(commandInputVariants({ inputSize }), className)}
        {...props}
      />
      {endAdornment}
    </div>
  )
}

const commandListVariants = cva(
  'scroll-py-1 overflow-x-hidden overflow-y-auto',
  {
    variants: {
      height: {
        auto: 'max-h-[300px]',
        fixed: 'h-88 max-h-none',
      },
    },
    defaultVariants: { height: 'auto' },
  }
)

function CommandList({
  className,
  height,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List> &
  VariantProps<typeof commandListVariants>) {
  return (
    <CommandPrimitive.List
      data-slot='command-list'
      data-height={height ?? 'auto'}
      className={cn(commandListVariants({ height }), className)}
      {...props}
    />
  )
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot='command-empty'
      className='py-6 text-center text-sm'
      {...props}
    />
  )
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot='command-group'
      className={cn(
        'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
        className
      )}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot='command-separator'
      className={cn('-mx-1 h-px bg-border', className)}
      {...props}
    />
  )
}

const commandItemVariants = cva(
  "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
  {
    variants: {
      align: {
        default: '',
        center: 'justify-center',
      },
      current: {
        true: 'bg-accent text-accent-foreground',
      },
    },
    defaultVariants: {
      align: 'default',
      current: false,
    },
  }
)

function CommandItem({
  className,
  align,
  current,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item> &
  VariantProps<typeof commandItemVariants>) {
  return (
    <CommandPrimitive.Item
      data-slot='command-item'
      aria-current={current ? 'page' : props['aria-current']}
      className={cn(commandItemVariants({ align, current }), className)}
      {...props}
    />
  )
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='command-shortcut'
      className={cn(
        'ml-auto text-xs tracking-widest text-muted-foreground',
        className
      )}
      {...props}
    />
  )
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
}
